// src/components/ui/HamburgerMenu.jsx
import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { CartContext } from '../../contexts/CartContext';
import logoImage from '../../assets/logo.png';
import casierIcon from '../../assets/icone_bdc_64.png';
import '../../styles/hamburger-menu.css';

const HamburgerMenu = ({ isOpen, onClose }) => {
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const { getTotalItems } = React.useContext(CartContext);
  
  const totalItems = getTotalItems();
  
  // État pour contrôler l'affichage de la modale de déconnexion
  const [showLogoutModal, setShowLogoutModal] = useState(false);
  const [isLoggingOut, setIsLoggingOut] = useState(false);

  // Navigation avec fermeture automatique du menu
  const handleNavigation = (path) => {
    onClose(); // Fermer le menu immédiatement
    setTimeout(() => {
      navigate(path); // Naviguer après l'animation de fermeture
    }, 500);
  };

  // Ouvrir la modale de confirmation de déconnexion
  const handleLogoutClick = () => {
    setShowLogoutModal(true);
  };

  // Fermer la modale sans déconnexion
  const handleCancelLogout = () => {
    setShowLogoutModal(false);
  };

  // Confirmer la déconnexion
  const handleConfirmLogout = async () => {
    setIsLoggingOut(true);
    try {
      await logout();
      // La fonction logout() dans AuthContext gère déjà :
      // - L'appel API de déconnexion
      // - Le nettoyage du localStorage
      // - La redirection vers /register
    } catch (error) {
      console.error('Erreur lors de la déconnexion:', error);
      setIsLoggingOut(false);
      setShowLogoutModal(false);
    }
  };

  // Fermer le menu si on clique sur l'overlay
  const handleOverlayClick = () => {
    onClose();
  };

  // ✅ SOLUTION : Toujours rendre le menu, laisser CSS gérer la transition
  // Pas de "if (!isOpen) return null;" !
  
  return (
    <>
      {/* Overlay sombre - classe "active" contrôle la visibilité */}
      <div 
        className={`menu-overlay ${isOpen ? 'active' : ''}`}
        onClick={handleOverlayClick}
        aria-hidden={!isOpen}
        style={{ pointerEvents: isOpen ? 'auto' : 'none' }}
      />
      
      {/* Menu latéral - classe "open" contrôle le slide */}
      <div 
        className={`hamburger-menu ${isOpen ? 'open' : ''}`}
        aria-hidden={!isOpen}
      >
        {/* EN-TÊTE : Logo centré sur fond blanc */}
        <div className="menu-header">
          {/* Bouton fermer en haut à droite */}
          <button 
            onClick={onClose}
            className="menu-close-button"
            aria-label="Fermer le menu"
            tabIndex={isOpen ? 0 : -1}
          >
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M18 6L6 18M6 6l12 12" />
            </svg>
          </button>
          
          {/* Logo centré */}
          <img src={logoImage} alt="Boissons du Cameroun" className="menu-logo" />
        </div>

        {/* SECTION COMPTE FUSIONNÉE : Avatar + Salutation + Sous-titre */}
        <div className="menu-account-section">
          {/* Avatar utilisateur */}
          <div className="menu-user-avatar">
            <svg width="32" height="32" viewBox="0 0 24 24" fill="currentColor">
              <path d="M12 12c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4zm0 2c-2.67 0-8 1.34-8 4v2h16v-2c0-2.66-5.33-4-8-4z" />
            </svg>
          </div>
          
          {/* Salutation */}
          <p className="menu-greeting-text">
            Bonjour {user?.prenom || 'Utilisateur'} !
          </p>
          
          {/* Sous-titre "Votre compte" */}
          <p className="menu-account-subtitle">
            Votre compte
          </p>
        </div>

        {/* Options du menu */}
        <div className="menu-options">
          {/* Mes commandes */}
          <button 
            className="menu-option"
            onClick={() => handleNavigation('/my-orders')}
            tabIndex={isOpen ? 0 : -1}
          >
            <div className="menu-option-icon">
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M21 16V8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16z" />
                <polyline points="3.27 6.96 12 12.01 20.73 6.96" />
                <line x1="12" y1="22.08" x2="12" y2="12" />
              </svg>
            </div>
            <span className="menu-option-text">Mes commandes</span>
            <div className="menu-option-arrow">
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M9 18l6-6-6-6" />
              </svg>
            </div>
          </button>

          {/* Mon casier */}
          <button 
            className="menu-option"
            onClick={() => handleNavigation('/cart')}
            tabIndex={isOpen ? 0 : -1}
          >
            <div className="menu-option-icon menu-cart-icon">
              <img src={casierIcon} alt="Casier" className="menu-cart-image" />
              {totalItems > 0 && (
                <span className="menu-cart-badge">{totalItems > 99 ? '99+' : totalItems}</span>
              )}
            </div>
            <span className="menu-option-text">Mon casier</span>
            <div className="menu-option-arrow">
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M9 18l6-6-6-6" />
              </svg>
            </div>
          </button>

          {/* Mon compte / Mes infos */}
          <button 
            className="menu-option"
            onClick={() => handleNavigation('/my-account')}
            tabIndex={isOpen ? 0 : -1}
          >
            <div className="menu-option-icon">
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" />
                <circle cx="12" cy="7" r="4" />
              </svg>
            </div>
            <span className="menu-option-text">Mon compte / Mes infos</span>
            <div className="menu-option-arrow">
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M9 18l6-6-6-6" />
              </svg>
            </div>
          </button>

          {/* Se déconnecter - Style rouge */}
          <button 
            className="menu-option menu-option-logout"
            onClick={handleLogoutClick}
            tabIndex={isOpen ? 0 : -1}
          >
            <div className="menu-option-icon">
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4" />
                <polyline points="16 17 21 12 16 7" />
                <line x1="21" y1="12" x2="9" y2="12" />
              </svg>
            </div>
            <span className="menu-option-text">Se déconnecter</span>
          </button>
        </div>
      </div>

      {/* MODALE DE CONFIRMATION DE DÉCONNEXION */}
      {showLogoutModal && (
        <div 
          className="logout-modal-overlay"
          onClick={handleCancelLogout}
          aria-modal="true"
          role="dialog"
          aria-labelledby="logout-modal-title"
        >
          <div 
            className="logout-modal"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Icône de déconnexion */}
            <div className="logout-modal-icon">
              <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4" />
                <polyline points="16 17 21 12 16 7" />
                <line x1="21" y1="12" x2="9" y2="12" />
              </svg>
            </div>
            
            {/* Titre */}
            <h2 id="logout-modal-title" className="logout-modal-title">
              Déconnexion
            </h2>
            
            {/* Message */}
            <p className="logout-modal-message">
              Êtes-vous sûr de vouloir vous déconnecter de votre compte ?
            </p>
            
            {/* Boutons */}
            <div className="logout-modal-buttons">
              <button 
                className="logout-modal-btn logout-modal-btn-cancel"
                onClick={handleCancelLogout}
                disabled={isLoggingOut}
              >
                Annuler
              </button>
              <button 
                className="logout-modal-btn logout-modal-btn-confirm"
                onClick={handleConfirmLogout}
                disabled={isLoggingOut}
              >
                {isLoggingOut ? (
                  <span className="logout-spinner"></span>
                ) : (
                  'Confirmer'
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default HamburgerMenu;